import type { PostHog } from 'posthog-js';
import { env } from '@/shared/config/env';
import { type ConsentChoice, consentCookie, readConsent } from './consent';

// GA4 and PostHog behind one module, with the website's property and project. Both cookies sit on
// macswarm.com (GA's `auto` cookie domain, PostHog's cross_subdomain_cookie), so a visit that starts on the
// website and signs in here is one visitor and one session. Events report only from VITE_ANALYTICS_HOSTS;
// elsewhere they go to the console.

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const POSTHOG_UI_HOST = 'https://us.posthog.com';

const state: {
  isLive: boolean;
  choice: ConsentChoice;
  posthog: PostHog | null;
  queue: Array<(posthog: PostHog) => void>;
} = { isLive: false, choice: 'denied', posthog: null, queue: [] };

/** Drops empty values and cuts strings to GA's 100-character limit, so both tools get the same properties. */
export function cleanProperties(properties: AnalyticsProperties): AnalyticsProperties {
  const clean: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null || value === '') continue;
    clean[key] = typeof value === 'string' ? value.slice(0, 100) : value;
  }
  return clean;
}

/** The sign-in link carries its one-time token in the fragment; analytics never sees a fragment. */
export function withoutFragment(url: string): string {
  const index = url.indexOf('#');
  return index === -1 ? url : url.slice(0, index);
}

/** Called once from main.tsx. */
export function initAnalytics(): void {
  state.choice = readConsent();
  state.isLive = env.VITE_ANALYTICS_HOSTS.includes(window.location.hostname);
  if (!state.isLive) return;
  if (env.VITE_GA_MEASUREMENT_ID) startGoogleAnalytics(env.VITE_GA_MEASUREMENT_ID);
  if (env.VITE_POSTHOG_TOKEN) {
    const load = () => void startPostHog(env.VITE_POSTHOG_TOKEN);
    // After first paint, like the website; Safari has no requestIdleCallback.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: 3000 });
    } else {
      setTimeout(load, 1);
    }
  }
}

export function track(name: string, properties: AnalyticsProperties = {}): void {
  const clean = cleanProperties(properties);
  if (!state.isLive) {
    console.debug('[analytics]', name, clean);
    return;
  }
  window.gtag?.('event', name, clean);
  withPostHog((posthog) => posthog.capture(name, clean));
}

/** Ties this browser's visits to the signed-in person: the internal user id, never the email, and only with consent. */
export function identify(userId: string): void {
  if (!state.isLive || state.choice !== 'granted') return;
  window.gtag?.('set', { user_id: userId });
  withPostHog((posthog) => {
    if (posthog.get_distinct_id() !== userId) posthog.identify(userId);
  });
}

/** After sign-out the next person on this browser starts anonymous. */
export function resetIdentity(): void {
  if (!state.isLive) return;
  window.gtag?.('set', { user_id: null });
  withPostHog((posthog) => posthog.reset());
}

/** The banner's answer: stored for the website too, applied to both tools now; a no deletes what was stored. */
export function setConsent(choice: ConsentChoice): void {
  state.choice = choice;
  // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is missing in Safari and Firefox
  document.cookie = consentCookie(choice, window.location.hostname);
  if (state.isLive) {
    window.gtag?.('consent', 'update', { analytics_storage: choice });
    withPostHog((posthog) =>
      posthog.set_config({
        persistence: choice === 'granted' ? 'localStorage+cookie' : 'memory',
        disable_session_recording: choice !== 'granted',
      }),
    );
    if (choice === 'denied') forgetStoredIds();
  }
  track('consent_updated', { choice });
}

// GA's _ga cookies and PostHog's ph_ cookie and storage, wherever they were set (this host or macswarm.com).
function forgetStoredIds(): void {
  for (const pair of document.cookie.split(';')) {
    const name = pair.split('=')[0]?.trim() ?? '';
    if (!/^(_ga|ph_)/.test(name)) continue;
    for (const domain of ['', `; domain=${window.location.hostname}`, '; domain=.macswarm.com']) {
      // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is missing in Safari and Firefox
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }
  }
  for (const store of [window.localStorage, window.sessionStorage]) {
    for (const key of Object.keys(store).filter((k) => k.startsWith('ph_'))) store.removeItem(key);
  }
}

function withPostHog(run: (posthog: PostHog) => void): void {
  if (!env.VITE_POSTHOG_TOKEN) return;
  if (state.posthog) run(state.posthog);
  else state.queue.push(run);
}

function startGoogleAnalytics(measurementId: string): void {
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads Arguments objects from the data layer, so this must stay a function using `arguments`.
  window.gtag = function gtag() {
    // biome-ignore lint/complexity/noArguments: gtag.js expects the Arguments object itself
    window.dataLayer?.push(arguments);
  };
  // No advertising features, whatever the visitor chose.
  window.gtag('consent', 'default', {
    analytics_storage: state.choice,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { page_location: withoutFragment(window.location.href) });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

async function startPostHog(token: string): Promise<void> {
  const { default: posthog } = await import('posthog-js');
  const isGranted = state.choice === 'granted';
  posthog.init(token, {
    // The website's Worker relays macswarm.com/ph/* to PostHog, so blockers that drop *.posthog.com let it through.
    api_host: env.VITE_POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: '2026-08-30',
    persistence: isGranted ? 'localStorage+cookie' : 'memory',
    cross_subdomain_cookie: true,
    disable_session_recording: !isGranted,
    person_profiles: 'identified_only',
    before_send: (event) => {
      if (event?.properties) {
        for (const key of ['$current_url', '$referrer', '$initial_current_url']) {
          const value = event.properties[key];
          if (typeof value === 'string') event.properties[key] = withoutFragment(value);
        }
      }
      return event;
    },
  });
  state.posthog = posthog;
  for (const run of state.queue.splice(0)) run(posthog);
}
